// src/component/MyPage.jsx
import React, {
  memo,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { auth, db } from "../api/firebase";
import {
  doc,
  onSnapshot,
  updateDoc,
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Stack,
  IconButton,
  Button,
  Snackbar,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import { AppThemeContext } from "./AppShell";
import EditNameDialog from "./EditNameDialog";
import BirthDayEditDialog from "./BirthDayEditDialog";
import PrefectureDialog from "./PrefectureDialog";
import { CelebrationAnimation } from "./CelebrationAnimation";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import MyProfileAvatar from "./MyProfileAvatar";

// ★ 外部コンポーネント（分離後）
import SekaiMuraVerificationCard from "./SekaiMuraVerificationCard";
import GroupActivity2026MyPageSection from "./GroupActivity2026MyPageSection";

import Switch from "@mui/material/Switch";
import FormGroup from "@mui/material/FormGroup";

// ===================== 自己紹介編集ダイアログ =====================

function SelfIntroductionEditDialog({
  open,
  onClose,
  initialText,
  onSave,
  accent = "#5A35FF",
}) {
  const [value, setValue] = useState(initialText || "");

  useEffect(() => {
    if (open) setValue(initialText || "");
  }, [open, initialText]);

  const handleSave = () => {
    const trimmed = value.trim();
    onSave(trimmed);
  };

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title="自己紹介を編集"
      accent={accent}
      actions={
        <>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!value.trim()}
            sx={{ bgcolor: accent, "&:hover": { bgcolor: accent } }}
          >
            保存する
          </Button>
        </>
      }
    >
      <TextField
        multiline
        minRows={5}
        fullWidth
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="あなたのこと・好きなこと・今のテーマなどを書いてみてください。"
        sx={{ mt: 1.5 }}
      />
    </DialogBase>
  );
}

// 汎用ダイアログベース
function DialogBase({ open, onClose, title, accent, children, actions }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow:
            "0 22px 60px rgba(15,10,40,0.35), 0 0 0 1px rgba(255,255,255,0.8)",
          background:
            "radial-gradient(circle at 0% 0%, #ffffff, #fdf7ff 45%, #f7fbff 100%)",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          fontWeight: 800,
          fontSize: 18,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            bgcolor: `${accent}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 16, color: accent }} />
        </Box>
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1.5 }}>{children}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>{actions}</DialogActions>
    </Dialog>
  );
}

// ===================== UID カード =====================

function UidCard({ uid, accent, onCopied }) {
  const handleCopy = async () => {
    if (!uid) return;
    try {
      await navigator.clipboard.writeText(uid);
      onCopied && onCopied();
    } catch (e) {
      console.error("UID copy failed:", e);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: 3,
        p: 0.2,
        background: `linear-gradient(135deg, ${accent}AA, ${accent}33, #ffffff)`,
        boxShadow: "0 16px 40px rgba(15,10,40,0.25)",
        maxWidth: 420,
      }}
    >
      <Box
        sx={{
          borderRadius: 2.6,
          bgcolor: "rgba(255,255,255,0.98)",
          px: 2,
          py: 1.8,
          display: "flex",
          alignItems: "center",
          gap: 1.4,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: `radial-gradient(circle at 30% 20%, #fff, ${accent}AA 40%, ${accent} 80%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              "0 10px 22px rgba(0,0,0,0.18), 0 0 0 3px rgba(255,255,255,0.9)",
            flexShrink: 0,
          }}
        >
          <ContentCopyRoundedIcon
            sx={{ fontSize: 20, color: "rgba(255,255,255,0.95)" }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(40,40,60,0.6)",
              mb: 0.2,
            }}
          >
            MEMBER UID
          </Typography>
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: 13,
              fontWeight: 700,
              color: "rgba(20,20,40,0.95)",
              wordBreak: "break-all",
              overflowWrap: "break-word",
            }}
          >
            {uid || "UID がありません"}
          </Typography>
        </Box>

        <Button
          variant="text"
          size="small"
          onClick={handleCopy}
          sx={{
            textTransform: "none",
            fontSize: 12,
            fontWeight: 700,
            color: accent,
          }}
        >
          コピー
        </Button>
      </Box>
    </Box>
  );
}

// ===================== メイン MyProfile コンポーネント =====================

const MyProfile = () => {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const textColor = theme.railText || "rgba(40,40,60,0.9)";

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editBirthOpen, setEditBirthOpen] = useState(false);
  const [prefectureDialogOpen, setPrefectureDialogOpen] = useState(false);
  const [selfIntroDialogOpen, setSelfIntroDialogOpen] = useState(false);

  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState("");

  // 一斉メール設定
  const [useDefaultEmail, setUseDefaultEmail] = useState(true);
  const [customEmail, setCustomEmail] = useState("");

  // ★ DDP設定（最大1000文字）
  const [ddpSetting, setDdpSetting] = useState("");
  const [ddpDocId, setDdpDocId] = useState(null); // users/{uid}/ddps の doc ID
  const [ddpLoading, setDdpLoading] = useState(true);
  const DDP_MAX_LENGTH = 1000;

  const [ddpProfileEnabled, setDdpProfileEnabled] = useState(false);

  // 音楽キャッシュクリアダイアログ（1日3回制限）
  const CACHE_CLEAR_KEY = "mm_audio_cache_cleared_at";
  const CACHE_CLEAR_WINDOW_MS = 24 * 60 * 60 * 1000; // 24時間
  const CACHE_CLEAR_MAX_PER_WINDOW = 3;

  const [audioCacheClearOpen, setAudioCacheClearOpen] = useState(false);

  // 直近24時間以内の実行時刻一覧（旧形式：単一タイムスタンプ文字列も読める）
  const readCacheClearTimes = () => {
    try {
      const raw = localStorage.getItem(CACHE_CLEAR_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const list = Array.isArray(parsed) ? parsed : [parsed];
      return list
        .map(Number)
        .filter((t) => Number.isFinite(t) && Date.now() - t < CACHE_CLEAR_WINDOW_MS);
    } catch (_) {
      return [];
    }
  };

  const isCacheClearOnCooldown = () => {
    return readCacheClearTimes().length >= CACHE_CLEAR_MAX_PER_WINDOW;
  };

  const handleClearAudioCache = async () => {
    try {
      const times = readCacheClearTimes();
      times.push(Date.now());
      localStorage.setItem(CACHE_CLEAR_KEY, JSON.stringify(times));
    } catch (_) {}
    try {
      await addDoc(collection(db, "audioCacheClearLog"), {
        userId: auth.currentUser?.uid || null,
        userAgent: navigator.userAgent || null,
        createdAt: serverTimestamp(),
      });
    } catch (_) {}
    try {
      await caches.delete("mm-audio-cache-v1");
    } catch (_) {}
    window.location.reload();
  };

  // --- Firestore 読み込み ---
  useEffect(() => {
    const current = auth.currentUser;
    if (!current) {
      setLoading(false);
      return;
    }

    const ref = doc(db, "users", current.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setUser(data);

          const defaultEmail = data.email;
          const currentBroadcast =
            data.broadcastReplyEmail && data.broadcastReplyEmail.trim()
              ? data.broadcastReplyEmail
              : defaultEmail;

          setUseDefaultEmail(
            !data.broadcastReplyEmail ||
              data.broadcastReplyEmail === defaultEmail
          );
          setCustomEmail(currentBroadcast || "");
          setDdpProfileEnabled(Boolean(data.ddpProfileEnabled));
        } else {
          console.error("User document does not exist");
        }
        setLoading(false);
      },
      (err) => {
        console.error("MyPage onSnapshot error:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  // --- DDP サブコレクション読み込み ---
  useEffect(() => {
    const current = auth.currentUser;
    if (!current) {
      setDdpLoading(false);
      return;
    }

    const loadDdp = async () => {
      try {
        const colRef = collection(db, "users", current.uid, "ddps");
        const snap = await getDocs(colRef);

        if (!snap.empty) {
          const first = snap.docs[0];
          const data = first.data();

          setDdpDocId(first.id);
          setDdpSetting(data.ddp || "");
        } else {
          setDdpDocId(null);
          setDdpSetting("");
        }
      } catch (e) {
        console.error("Failed to load DDP doc:", e);
      } finally {
        setDdpLoading(false);
      }
    };

    loadDdp();
  }, []);

  const showSnack = useCallback((msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // --- 更新系ハンドラ ---

  const updateUserField = async (fields) => {
    const current = auth.currentUser;
    if (!current) return;
    try {
      await updateDoc(doc(db, "users", current.uid), fields);
    } catch (e) {
      console.error("Failed to update user fields:", e);
      showSnack("更新に失敗しました。時間をおいて再度お試しください。");
    }
  };

  const handleNameSave = async (name) => {
    await updateUserField({ name });
    showSnack("名前を更新しました。");
    setEditNameOpen(false);
  };

  const handleBirthSave = async ({ year, month, day }) => {
    await updateUserField({
      birthyear: year,
      birthmonth: month,
      birthday: day,
    });
    showSnack("生年月日を更新しました。");
    setEditBirthOpen(false);
  };

  const handleGenderChange = async (e) => {
    await updateUserField({ sextype: e.target.value });
    showSnack("性別を更新しました。");
  };

  const handleBloodChange = async (e) => {
    await updateUserField({ bloodtype: e.target.value });
    showSnack("血液型を更新しました。");
  };

  const handlePrefectureUpdated = (message) => {
    setCelebrationMessage(message);
    setCelebrationOpen(true);
    setTimeout(() => setCelebrationOpen(false), 8000);
    setPrefectureDialogOpen(false);
  };

  const handleSelfIntroSave = async (text) => {
    await updateUserField({ selfIntroduction: text });
    showSnack("自己紹介を更新しました。");
    setSelfIntroDialogOpen(false);
  };

  const handleEmailPreferenceChange = (e) => {
    const value = e.target.value;
    setUseDefaultEmail(value === "default");
  };

  const handleCustomEmailSave = async () => {
    const emailToSave = useDefaultEmail
      ? user?.email || ""
      : (customEmail || "").trim();

    if (!emailToSave) {
      showSnack("メールアドレスを入力してください。");
      return;
    }

    await updateUserField({ broadcastReplyEmail: emailToSave });
    showSnack("一斉メールの設定を更新しました。");
  };

  const handleUidCopied = () => {
    showSnack("UID をコピーしました。");
  };

  // ★ DDP設定 保存（users/{uid}/ddps の 1ドキュメントを更新 or 新規作成）
  const handleDdpSettingSave = async () => {
    const current = auth.currentUser;
    if (!current) return;

    const text = ddpSetting.trim();
    if (!text) {
      showSnack("DDPの内容を入力してください。");
      return;
    }

    try {
      const colRef = collection(db, "users", current.uid, "ddps");

      if (ddpDocId) {
        await updateDoc(doc(colRef, ddpDocId), {
          ddp: text,
          updatedAt: serverTimestamp(),
        });
      } else {
        const newRef = await addDoc(colRef, {
          ddp: text,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setDdpDocId(newRef.id);
      }

      showSnack("DDPを保存しました。");
    } catch (e) {
      console.error("Failed to save ddp:", e);
      showSnack("DDPの保存に失敗しました。時間をおいて再度お試しください。");
    }
  };

  const handleDdpProfileEnabledChange = async (e) => {
    const next = e.target.checked;
    setDdpProfileEnabled(next);
    await updateUserField({ ddpProfileEnabled: next });
    showSnack(
      next
        ? "DDPをプロフィールで公開します。"
        : "DDPのプロフィール公開をオフにしました。"
    );
  };

  // --- 表示用整形 ---

  const birthLabel =
    user && user.birthyear && user.birthmonth && user.birthday
      ? `${user.birthyear}/${user.birthmonth}/${user.birthday}`
      : "未登録";

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!auth.currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>ログイン情報が確認できません。</Typography>
      </Box>
    );
  }

  const uid = auth.currentUser.uid;

  return (
    <Box sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 } }}>
      <Container maxWidth="md" sx={{ px: { xs: 0.5, md: 2 } }}>
        {/* ============ ヘッダー ============ */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2, md: 3 },
            mb: 3,
            background: `radial-gradient(circle at 0% 0%, ${surface}, #ffffff 40%, #f4f6ff 100%)`,
            boxShadow: "0 18px 40px rgba(15,10,40,0.18)",
          }}
        >
          <Grid container spacing={2.5} alignItems="center">
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <MyProfileAvatar userId={uid} size={120} editable />
            </Grid>

            <Grid item xs={12} sm={8}>
              <Stack spacing={1}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 800,
                      letterSpacing: "0.06em",
                      color: textColor,
                    }}
                  >
                    {user?.name || "おなまえ未設定"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <PlaceRoundedIcon
                    sx={{ fontSize: 18, color: "rgba(60,60,90,0.6)" }}
                  />
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "rgba(60,60,90,0.8)",
                    }}
                  >
                    {user?.prefecture || "居住地 未登録"}
                  </Typography>
                </Box>

                <UidCard uid={uid} accent={accent} onCopied={handleUidCopied} />
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* ============ 基本情報 ============ */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2, md: 3 },
            mb: 3,
            bgcolor: surface,
            boxShadow: "0 10px 26px rgba(15,10,40,0.08)",
          }}
        >
          <SectionHeader title="基本情報" accent={accent} />

          <Grid container spacing={2.5}>
            {/* 名前 */}
            <Grid item xs={12} sm={6}>
              <FieldLabel>名前</FieldLabel>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ flexGrow: 1 }}>
                  {user?.name || "未登録"}
                </Typography>
                <IconButton size="small" onClick={() => setEditNameOpen(true)}>
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>

            {/* 生年月日 */}
            <Grid item xs={12} sm={6}>
              <FieldLabel>生年月日</FieldLabel>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ flexGrow: 1 }}>{birthLabel}</Typography>
                <IconButton size="small" onClick={() => setEditBirthOpen(true)}>
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>

            {/* 性別 */}
            <Grid item xs={12} sm={6}>
              <FieldLabel>性別</FieldLabel>
              <RadioGroup
                row
                name="sextype"
                value={user?.sextype || ""}
                onChange={handleGenderChange}
              >
                <FormControlLabel
                  value="男性"
                  control={<Radio size="small" />}
                  label="男性"
                />
                <FormControlLabel
                  value="女性"
                  control={<Radio size="small" />}
                  label="女性"
                />
                <FormControlLabel
                  value="回答しない"
                  control={<Radio size="small" />}
                  label="回答しない"
                />
              </RadioGroup>
            </Grid>

            {/* 血液型 */}
            <Grid item xs={12} sm={6}>
              <FieldLabel>血液型</FieldLabel>
              <RadioGroup
                row
                name="bloodtype"
                value={user?.bloodtype || ""}
                onChange={handleBloodChange}
              >
                <FormControlLabel
                  value="A"
                  control={<Radio size="small" />}
                  label="A型"
                />
                <FormControlLabel
                  value="B"
                  control={<Radio size="small" />}
                  label="B型"
                />
                <FormControlLabel
                  value="O"
                  control={<Radio size="small" />}
                  label="O型"
                />
                <FormControlLabel
                  value="AB"
                  control={<Radio size="small" />}
                  label="AB型"
                />
                <FormControlLabel
                  value="わからない"
                  control={<Radio size="small" />}
                  label="わからない"
                />
              </RadioGroup>
            </Grid>
          </Grid>
        </Paper>

        {/* ============ 自己紹介 ============ */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2, md: 3 },
            mb: 3,
            bgcolor: surface,
            boxShadow: "0 10px 26px rgba(15,10,40,0.08)",
          }}
        >
          <SectionHeader
            title="自己紹介"
            accent={accent}
            action={
              <Button
                size="small"
                startIcon={<EditRoundedIcon fontSize="small" />}
                onClick={() => setSelfIntroDialogOpen(true)}
                sx={{
                  textTransform: "none",
                  fontSize: 12,
                  color: accent,
                }}
              >
                編集
              </Button>
            }
          />

          <Box
            sx={{
              mt: 1,
              p: 2,
              borderRadius: 2,
              border: "1px dashed rgba(120,120,160,0.35)",
              bgcolor: "rgba(255,255,255,0.7)",
              minHeight: 80,
              whiteSpace: "pre-wrap",
              fontSize: 14,
              lineHeight: 1.8,
              color: "rgba(40,40,60,0.9)",
            }}
          >
            {user?.selfIntroduction?.trim()
              ? user.selfIntroduction
              : "まだ自己紹介が登録されていません。編集ボタンから、ひとこと書いてみましょう。"}
          </Box>
        </Paper>

        {/* ============ DDP設定 ============ */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2, md: 3 },
            mb: 3,
            bgcolor: surface,
            boxShadow: "0 10px 26px rgba(15,10,40,0.08)",
          }}
        >
          <SectionHeader title="DDP" accent={accent} />
          <Box sx={{ mt: 1 }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={ddpProfileEnabled}
                    onChange={handleDdpProfileEnabledChange}
                  />
                }
                label={
                  <Typography
                    sx={{ fontSize: 13, color: "rgba(60,60,90,0.85)" }}
                  >
                    このDDPをプロフィールで表示する
                  </Typography>
                }
              />
            </FormGroup>
          </Box>

          {ddpLoading ? (
            <Typography
              sx={{ fontSize: 13, color: "rgba(80,80,110,0.8)", mt: 1 }}
            >
              DDPを読み込み中です…
            </Typography>
          ) : (
            <>
              <TextField
                multiline
                minRows={6}
                fullWidth
                value={ddpSetting}
                onChange={(e) => setDdpSetting(e.target.value)}
                inputProps={{ maxLength: DDP_MAX_LENGTH }}
                placeholder="例）2026年末までに実現したいこと、そのために大事にしたい在り方、毎週意識したい行動などを自由に書いてください。"
                sx={{ mt: 1 }}
                helperText={`${ddpSetting.length}/${DDP_MAX_LENGTH} 文字`}
              />

              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleDdpSettingSave}
                  sx={{ bgcolor: accent, "&:hover": { bgcolor: accent } }}
                >
                  保存
                </Button>
              </Box>
            </>
          )}
        </Paper>

        {/* ============ 2026 グループ活動 参加意思 ============ */}
        <GroupActivity2026MyPageSection />

        {/* ============ 連絡先情報 / 一斉メール設定 ============ */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2, md: 3 },
            mb: 4,
            bgcolor: surface,
            boxShadow: "0 10px 26px rgba(15,10,40,0.08)",
          }}
        >
          <SectionHeader
            title="連絡先・一斉メール設定"
            accent={accent}
            icon={<EmailRoundedIcon sx={{ fontSize: 18, color: accent }} />}
          />

          <Grid container spacing={2.5}>
            {/* 登録時の情報（読み取り専用） */}
            <Grid item xs={12} md={6}>
              <FieldLabel>登録時の情報</FieldLabel>
              <Box sx={{ fontSize: 13.5, lineHeight: 1.8 }}>
                <div>登録名：{user?.registrationName || "未登録"}</div>
                <div>
                  登録メール：
                  {user?.registrationEmail || user?.email || "未登録"}
                </div>
                <div>登録電話番号：{user?.registrationPhone || "未登録"}</div>
              </Box>
            </Grid>

            {/* 一斉メール返信先設定 */}
            <Grid item xs={12} md={6}>
              <FieldLabel>一斉メールの返信先</FieldLabel>

              <RadioGroup
                name="emailPreference"
                value={useDefaultEmail ? "default" : "custom"}
                onChange={handleEmailPreferenceChange}
                sx={{ mb: 1 }}
              >
                <FormControlLabel
                  value="default"
                  control={<Radio size="small" />}
                  label={`ログインメール（${user?.email || "未登録"}）を使用`}
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" />}
                  label="別のメールアドレスを使用"
                />
              </RadioGroup>

              {!useDefaultEmail && (
                <TextField
                  fullWidth
                  size="small"
                  label="メールアドレス"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  sx={{ mb: 1.5 }}
                />
              )}

              <Button
                variant="contained"
                size="small"
                onClick={handleCustomEmailSave}
                sx={{ bgcolor: accent, "&:hover": { bgcolor: accent } }}
              >
                保存
              </Button>

              <Typography
                sx={{
                  mt: 1,
                  fontSize: 11,
                  color: "rgba(80,80,110,0.8)",
                }}
              >
                マスターマインドからの一斉メールに対して、
                参加者から返信が届くアドレスをここで指定できます。
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* ============ セカイムラ認証（最下部） ============ */}
        {/* 非表示化：セカイムラ認証カードはマイページから一旦外す
        <SekaiMuraVerificationCard
          user={user}
          userId={uid}
          accent={accent}
          showSnack={showSnack}
        />
        */}

        {/* ============ 音が出ない方向けキャッシュクリア ============ */}
        <Box sx={{ mt: 3, mb: 12, textAlign: "center" }}>
          <Button
            variant="text"
            size="small"
            startIcon={<VolumeOffRoundedIcon />}
            disabled={isCacheClearOnCooldown()}
            onClick={() => setAudioCacheClearOpen(true)}
            sx={{
              fontSize: 12,
              color: "rgba(80,80,110,0.7)",
              textTransform: "none",
            }}
          >
            {isCacheClearOnCooldown()
              ? "音楽データは再取得済みです（1日3回まで）"
              : "音の出ない方はこちら"}
          </Button>
        </Box>
      </Container>

      {/* ---- Dialog / Snackbar / Effect ---- */}
      <EditNameDialog
        open={editNameOpen}
        handleClose={() => setEditNameOpen(false)}
        handleSave={handleNameSave}
        name={user?.name}
      />
      <BirthDayEditDialog
        open={editBirthOpen}
        handleClose={() => setEditBirthOpen(false)}
        handleSave={handleBirthSave}
        currentBirthDate={
          user
            ? `${user.birthyear || ""}-${user.birthmonth || ""}-${
                user.birthday || ""
              }`
            : ""
        }
      />
      <PrefectureDialog
        open={prefectureDialogOpen}
        handleClose={() => setPrefectureDialogOpen(false)}
        onPrefectureUpdated={handlePrefectureUpdated}
      />
      <SelfIntroductionEditDialog
        open={selfIntroDialogOpen}
        onClose={() => setSelfIntroDialogOpen(false)}
        initialText={user?.selfIntroduction || ""}
        onSave={handleSelfIntroSave}
        accent={accent}
      />
      <CelebrationAnimation
        isOpen={celebrationOpen}
        setIsOpen={setCelebrationOpen}
        message={celebrationMessage}
      />
      {/* 音楽キャッシュクリア確認ダイアログ */}
      <Dialog
        open={audioCacheClearOpen}
        onClose={() => setAudioCacheClearOpen(false)}
      >
        <DialogTitle>音楽データの再取得</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            壊れている可能性のある音楽データを消去して、新しくダウンロードします。{"\n"}
            その際、画面を再起動します。{"\n\n"}
            よろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAudioCacheClearOpen(false)}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={handleClearAudioCache}>
            再取得する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

// セクションのタイトル
function SectionHeader({ title, accent, action, icon }) {
  return (
    <Box
      sx={{
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 26,
            borderRadius: 999,
            background: `linear-gradient(180deg, ${accent}, ${accent}66)`,
          }}
        />
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: "0.06em",
            display: "flex",
            alignItems: "center",
            gap: 0.8,
          }}
        >
          {icon}
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
  );
}

function FieldLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 700,
        color: "rgba(80,80,110,0.9)",
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

export default memo(MyProfile);
